import { attachmentStickyStage } from "./conversationUserCardState";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "react-aria-components";
import type { ChatAttachmentResourceRef } from "./chatAttachments";
import { ConversationSentResources } from "./ConversationSentResources";
import fileIcon from "@/assets/figma/chat-file-drop/document-file.svg";

/** Original attachments keep their document space; the sticky question only projects a summary. */
export function ConversationUserCard({ children, turnId, stuck, compactSticky = false, summary = "已发送附件", resources = [], organizationId, onOpenResource }: {
  children: ReactNode;
  turnId: string;
  stuck: boolean;
  compactSticky?: boolean;
  summary?: string;
  resources?: ReadonlyArray<ChatAttachmentResourceRef>;
  organizationId: string;
  onOpenResource(id: string): void;
}) {
  const [previews, setPreviews] = useState<Readonly<Record<string, string>>>({});
  const receivePreview = useCallback((id: string, url: string) => {
    setPreviews((current) => current[id] === url ? current : { ...current, [id]: url });
  }, []);
  const cardRef = useRef<HTMLDivElement>(null);
  const compact = compactSticky && stuck;
  const [naturalHeight, setNaturalHeight] = useState(70);
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || compact) return;
    const measure = () => setNaturalHeight(card.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(card);
    return () => observer.disconnect();
  }, [compact]);
  const attachmentsRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<"question" | "attachments" | "complete">("question");
  useEffect(() => {
    const card = cardRef.current;
    const row = attachmentsRef.current;
    const scroller = card?.closest<HTMLElement>(".merry-conversation__scroll");
    if (!card || !row || !scroller) return;
    const update = () => {
      const line = card.getBoundingClientRect().bottom;
      const bounds = row.getBoundingClientRect();
      const next = attachmentStickyStage(bounds.top, bounds.bottom, line);
      setStage((current) => current === next ? current : next);
    };
    const observer = new ResizeObserver(update);
    observer.observe(row);
    scroller.addEventListener("scroll", update, { passive: true });
    update();
    return () => { observer.disconnect(); scroller.removeEventListener("scroll", update); };
  }, [resources.length]);
  const returnToQuestion = () => {
    cardRef.current?.closest<HTMLElement>("[data-conversation-turn-id]")?.scrollIntoView({
      block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };
  return <>
    <div className="merry-conversation-turn__user" data-conversation-user-card={turnId}
      data-stuck={stuck ? "true" : undefined} data-attachment-sticky-stage={stuck ? stage : "question"} ref={cardRef}>
      {compactSticky ? <span aria-hidden="true" className="merry-user-chat-message__glass" /> : null}
      <div className="conversation-user-card__content" hidden={compact} inert={compact}>{children}</div>
      {compact ? <Button className="conversation-user-card__return"
        aria-label={resources.length ? `返回本轮问题及 ${resources.length} 个附件` : "返回本轮问题"} onPress={returnToQuestion}>
        <span className="conversation-user-card__summary">{summary}</span>
        {stage !== "question" && resources.length ? <>
        <span aria-hidden="true" className="conversation-user-card__stack">
          {resources.slice(0, 3).map((resource, index) => <img alt="" src={previews[resource.resource_id] || fileIcon} key={resource.resource_id}
            style={{ transform: `rotate(${index % 2 ? 5 : -5}deg)` }} />)}
        </span><span className="conversation-user-card__count">{resources.length} 个附件</span></> : null}
      </Button> : null}
      {compactSticky ? <span aria-hidden="true" className="merry-user-chat-message__stroke" /> : null}
    </div>
    {compact && naturalHeight > 70 ? <div aria-hidden="true" className="conversation-user-card__space" style={{ height: naturalHeight - 70 }} /> : null}
    {resources.length ? <div className="conversation-user-card__attachments" ref={attachmentsRef}>
      <ConversationSentResources resources={resources} organizationId={organizationId} onOpenResource={onOpenResource} onPreview={receivePreview} />
    </div> : null}
  </>;
}
